import React, { useState, useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faSpinner, faList } from '@fortawesome/free-solid-svg-icons';
import {
  fetchPractices,
  deletePractice,
  setSearchTerm,
  setFilters,
  setSorting,
  clearFilters
} from '../../../store/slices/practiceSlice';
import { toast } from 'react-toastify';
import { useBreadcrumb } from '../../../contexts/BreadcrumbContext';
import EventLogSection from '../../../components/EventLogSection/EventLogSection';
import Table from '../../../components/Common/Table/Table';
import StatusBadge from '../../../components/Common/StatusBadge/StatusBadge';
import ActionButtons from '../../../components/Common/ActionButtons/ActionButtons';
import Heading from '../../../components/Common/Heading/Heading';
import ListScreenSearchBar from '../../../components/Common/ListScreenSearchBar/ListScreenSearchBar';
import ListScreenPaginationBar from '../../../components/Common/ListScreenPaginationBar/ListScreenPaginationBar';
import '../../../components/Common/ListScreenPageLayout/listScreenPage.css';
import './PracticesList.css';

const PracticesList = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Redux state (guard against undefined slice e.g. before rehydration)
  const {
    practices = [],
    pagination,
    isLoading,
    isDeleting,
    error,
    searchTerm = '',
    filters,
    sortBy,
    sortOrder
  } = useSelector(state => state.practice ?? {});

  const auth = useSelector(state => state.auth || {});
  const isAuthenticated = !!auth.isAuthenticated;
  const user = auth.user; // Get user from auth state
  const isSystemCaregiver = Number(user?.role_id) === 7;

  // Local state
  const [currentPage, setCurrentPage] = useState(1);
  const [localSearchTerm, setLocalSearchTerm] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [practiceToDelete, setPracticeToDelete] = useState(null);
  const [showLogSection, setShowLogSection] = useState(false);
  const [isListInitialized, setIsListInitialized] = useState(false);
  const { clearBreadcrumbs } = useBreadcrumb();

  useLayoutEffect(() => {
    clearBreadcrumbs();
  }, [clearBreadcrumbs]);

  useEffect(() => {
    if (user && Number(user.role_id) === 3) {
      navigate(`/practices/${user.practice_id || ''}`, { replace: true });
    }
  }, [user, navigate]);

  // Get status order for sorting (Active > Pending > Locked)
  const getStatusOrder = (status) => {
    if (!status || typeof status !== 'string') {
      return 99; // Unknown status goes last
    }
    const statusStr = status.toUpperCase();
    // Active = 'A' -> order 1
    if (statusStr === 'A' || statusStr === 'ACTIVE') {
      return 1;
    }
    // Pending = 'P' -> order 2
    if (statusStr === 'P' || statusStr === 'PENDING') {
      return 2;
    }
    // Locked = 'L' -> order 3
    if (statusStr === 'L' || statusStr === 'LOCKED') {
      return 3;
    }
    return 99; // Unknown status goes last
  };

  // Sort practices
  const sortedPractices = useMemo(() => {
    if (!practices || practices.length === 0) return [];

    return [...practices].sort((a, b) => {
      let valA = a[sortBy];
      let valB = b[sortBy];

      // Function to compare values
      const compare = () => {
        // 1. Status Sorting (Custom Order: Active > Pending > Locked)
        if (sortBy === 'status') {
          const orderA = getStatusOrder(a.status);
          const orderB = getStatusOrder(b.status);
          return orderA - orderB;
        }

        // 2. Phone Number Sorting (Numeric)
        if (sortBy === 'phone_1' || sortBy === 'phone_2') {
          const numA = valA ? parseInt(valA.replace(/\D/g, '')) || 0 : 0;
          const numB = valB ? parseInt(valB.replace(/\D/g, '')) || 0 : 0;
          return numA - numB;
        }

        // 3. Patient counts (numeric; API may return string/BigInt)
        if (sortBy === 'total_patients' || sortBy === 'active_patients' || sortBy === 'pending_patients' || sortBy === 'locked_patients') {
          const numA = Number(valA) || 0;
          const numB = Number(valB) || 0;
          return numA - numB;
        }

        // 4. Billing Period Sorting (Date-based)
        if (sortBy === 'billing_period') {
          const getDateValue = (str) => {
            if (!str) return 0;
            const match = str.match(/(\d{1,4}[-/]\d{1,2}[-/]\d{1,4})/);
            return match ? new Date(match[1]).getTime() : 0;
          };
          const dateA = getDateValue(valA);
          const dateB = getDateValue(valB);
          return dateA - dateB;
        }

        // 5. Default String/Alphanumeric Sorting
        if (typeof valA === 'string' && typeof valB === 'string') {
          return valA.localeCompare(valB, undefined, { numeric: true, sensitivity: 'base' });
        }

        // 6. Fallback
        if (valA < valB) return -1;
        if (valA > valB) return 1;
        return 0;
      };

      const result = compare();

      // Apply Sort Order
      const finalResult = sortOrder === 'asc' ? result : -result;

      // Tie-breaker: same status / same count / other ties → ascending practice id (stable, matches SQL list order)
      if (finalResult === 0) {
        return (Number(a.id) || 0) - (Number(b.id) || 0);
      }

      return finalResult;
    });
  }, [practices, sortBy, sortOrder]);


  // sessionStorage key used to remember whether we've already shown the login notification for the current authenticated session
  const STORAGE_KEY = 'practices_list_prev_auth_flag_v1';
  // prevAuthRef is used to detect transitions during the component lifecycle
  const prevAuthRef = useRef(null);

  // Initialize prevAuthRef from sessionStorage on first render (so navigation while logged-in won't re-show)
  useEffect(() => {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      prevAuthRef.current = stored === '1';
    } catch (err) {
      // if sessionStorage not available, default to false (will show once on mount if authenticated)
      prevAuthRef.current = false;
    }
  }, []);

  // Debounce search term for real-time filtering
  useEffect(() => {
    const timer = setTimeout(() => {
      if (localSearchTerm !== searchTerm) {
        dispatch(setSearchTerm(localSearchTerm));
        setCurrentPage(1);
      }
    }, 300); // 300ms debounce delay

    return () => clearTimeout(timer);
  }, [localSearchTerm, searchTerm, dispatch]);

  // Always open Practice List in a clean state and silently refresh full list.
  useEffect(() => {
    dispatch(clearFilters());
    dispatch(setSearchTerm(''));
    setLocalSearchTerm('');
    setCurrentPage(1);
    setIsListInitialized(true);
  }, [dispatch]);

  useEffect(() => {
    if (!isListInitialized) return;
    const limit = filters.per_page === 'all' || filters.per_page === 9999 ? 9999 : (filters.per_page || 30);
    const params = {
      page: currentPage,
      limit: limit,
      search: searchTerm,
      ...filters,
      sortBy,
      sortOrder
    };

    // For System Caregiver (Role 7), filter by their ID
    if (auth.user?.role_id === 7) {
      params.system_caregiver_id = auth.user.id;
    }

    // Replace 'all' with 9999 for API call
    if (params.per_page === 'all') {
      params.per_page = 9999;
    }
    dispatch(fetchPractices(params));
  }, [currentPage, searchTerm, filters, dispatch, auth.user, isListInitialized]);

  // Show notification only when we detect a real login event:
  // - if user becomes authenticated and prevAuthRef.current is false -> treat as new login
  // - set the sessionStorage flag so subsequent navigation doesn't re-show
  // - when user logs out (isAuthenticated false), clear the sessionStorage flag so future logins will show again
  useEffect(() => {
    try {
      if (!isAuthenticated) {
        // user logged out — clear flag so next login triggers
        sessionStorage.removeItem(STORAGE_KEY);
        prevAuthRef.current = false;
        return;
      }

      // isAuthenticated === true
      const alreadySeen = prevAuthRef.current === true;

      if (!alreadySeen) {
        // this is a genuine login (or first time we detect auth in this page)
        toast.success('Login successful');
        // persist that notification was shown for this auth session
        sessionStorage.setItem(STORAGE_KEY, '1');
        prevAuthRef.current = true;
      }
      // if alreadySeen === true -> don't show (it's navigation or a refresh while still logged in)
    } catch (err) {
      // fallback: if storage fails, still show once on login transition
      if (isAuthenticated && !prevAuthRef.current) {
        toast.success('Login successful');
        prevAuthRef.current = true;
      }
    }
  }, [isAuthenticated]);

  // Handle sorting
  const handleSort = (column) => {
    const newOrder = sortBy === column && sortOrder === 'asc' ? 'desc' : 'asc';
    dispatch(setSorting({ sortBy: column, sortOrder: newOrder }));
    setCurrentPage(1);
  };

  // Handle practice view
  const handleViewPractice = (practiceId) => {
    if (isSystemCaregiver) {
      navigate(`/practices/${practiceId}?tab=patient`);
      return;
    }
    navigate(`/practices/${practiceId}`);
  };

  // Handle view patients with status filter
  const handleViewPatients = (practiceId, statusFilter) => {
    // Map filter to status_id: 'all' = no filter, 'active' = 2, 'pending' = 3, 'locked' = 4
    const statusMap = {
      'all': null,
      'active': 2,
      'pending': 3,
      'locked': 4
    };
    const statusId = statusMap[statusFilter];

    // Navigate to patient list with status filter
    if (statusId) {
      navigate(`/practices/${practiceId}?tab=patient&status=${statusId}`);
    } else {
      navigate(`/practices/${practiceId}?tab=patient`);
    }
  };

  // Handle practice edit
  const handleEditPractice = (practiceId) => {
    const selectedPractice = (practices || []).find((p) => String(p.id) === String(practiceId)) || null;
    console.log('📝 Edit Practice clicked:', {
      practiceId,
      selectedPractice
    });
    navigate(`/practices/add?editId=${practiceId}`);
  };

  // Handle add practice click
  const handleAddPractice = () => {
    console.log('➕ Add Practice clicked');
    navigate('/practices/add');
  };

  // Handle practice delete
  const handleDeletePractice = (practice) => {
    setPracticeToDelete(practice);
    setShowDeleteModal(true);
  };

  const refetchPracticesList = () => {
    const limit =
      filters.per_page === 'all' || filters.per_page === 9999 ? 9999 : filters.per_page || 30;
    const params = {
      page: currentPage,
      limit,
      search: searchTerm,
      ...filters,
      sortBy,
      sortOrder
    };
    if (auth.user?.role_id === 7) {
      params.system_caregiver_id = auth.user.id;
    }
    if (params.per_page === 'all') {
      params.per_page = 9999;
    }
    dispatch(fetchPractices(params));
  };

  // Confirm delete — optimistic UI: toast + list update immediately; API runs in background
  const confirmDelete = () => {
    if (!practiceToDelete) return;

    const { id, practice_name: practiceName } = practiceToDelete;
    const displayName = (practiceName || 'Practice').trim() || 'Practice';

    setShowDeleteModal(false);
    setPracticeToDelete(null);
    toast.success(`${displayName} was deleted successfully.`);

    dispatch(deletePractice(id))
      .unwrap()
      .catch((err) => {
        const message = typeof err === 'string' ? err : err?.message || 'Failed to delete practice';
        toast.error(message);
        refetchPracticesList();
      });
  };

  // Cancel delete
  const cancelDelete = () => {
    setShowDeleteModal(false);
    setPracticeToDelete(null);
  };

  // Handle pagination
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Columns for the Table component
  const columns = useMemo(() => {
    if (isSystemCaregiver) {
      return [
        {
          key: 'index',
          sortKey: 'id',
          title: '#',
          sortable: true,
          render: (_, index) => index + 1
        },
        {
          key: 'practice_name',
          title: 'Practice Name',
          sortable: true,
          render: (practice) => (
            <button
              type="button"
              className="practices-list-name-link"
              onClick={() => handleViewPractice(practice.id)}
            >
              {practice.practice_name}
            </button>
          )
        },
        {
          key: 'email_1',
          title: 'Email',
          sortable: true,
          className: 'practices-list-email-cell',
          render: (practice) => practice.email_1 || 'N/A'
        },
        {
          key: 'phone_1',
          title: 'Phone 1',
          sortable: true,
          render: (practice) => practice.phone_1 || 'N/A'
        },
        {
          key: 'phone_2',
          title: 'Phone 2',
          sortable: true,
          render: (practice) => practice.phone_2 || 'N/A'
        },
        {
          key: 'address_1',
          title: 'Address',
          sortable: true,
          render: (practice) => practice.address_1 || 'N/A'
        },
        {
          key: 'fax_1',
          title: 'Fax',
          sortable: true,
          render: (practice) => practice.fax_1 || 'N/A'
        },
        {
          key: 'actions',
          title: 'Action',
          sortable: false,
          render: (practice) => (
            <ActionButtons
              onView={() => handleViewPractice(practice.id)}
              onEdit={() => handleEditPractice(practice.id)}
              onDelete={() => handleDeletePractice(practice)}
              hideEdit={isSystemCaregiver}
              hideDelete={isSystemCaregiver}
              isDeleting={isDeleting && practiceToDelete?.id === practice.id}
            />
          )
        }
      ];
    }

    // Default columns for other roles
    return [
      { key: 'id', title: 'ID', sortable: true },
      {
        key: 'status',
        title: 'Status',
        sortable: true,
        className: 'reusable-table-centered-col',
        render: (practice) => (
          <StatusBadge status={practice.status} />
        )
      },
      {
        key: 'practice_name',
        title: 'Name',
        sortable: true,
        render: (practice) => (
          <button
            type="button"
            className="practices-list-name-link"
            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit' }}
            onClick={() => handleViewPractice(practice.id)}
          >
            {practice.practice_name}
          </button>
        )
      },
      { key: 'phone_1', title: 'Phone 1', sortable: true, render: (practice) => practice.phone_1 || 'N/A' },
      { key: 'phone_2', title: 'Phone 2', sortable: true, render: (practice) => practice.phone_2 || 'N/A' },
      { key: 'email_1', title: 'Email', sortable: true, className: 'practices-list-email-cell', render: (practice) => practice.email_1 || 'N/A' },
      { key: 'billing_period', title: 'Billing Period Date', sortable: true, render: (practice) => practice.billing_period || 'N/A' },
      {
        key: 'total_patients',
        title: 'Patients Status',
        sortable: true,
        render: (practice) => (
          <div className="practice-patient-summary">
            <div
              className="summary-left"
              onClick={() => handleViewPatients(practice.id, "all")}
            >
              <div className="summary-total">Total: {practice.total_patients || 0}</div>
            </div>

            <div className="summary-divider-v"></div>

            <div className="summary-right">
              <div
                className="summary-item"
                onClick={() => handleViewPatients(practice.id, "active")}
              >
                <span className="summary-icon icon-active">
                  <i className="fa fa-check"></i>
                </span>
                <span className="summary-label">
                  Active (<span className="letter-a">A</span>):
                </span>
                <strong className="summary-count">{practice.active_patients || 0}</strong>
              </div>
              <div className="summary-divider-h"></div>

              <div
                className="summary-item"
                onClick={() => handleViewPatients(practice.id, "pending")}
              >
                <span className="summary-icon icon-pending">
                  <i className="fa fa-hourglass-half"></i>
                </span>
                <span className="summary-label">
                  Pending (<span className="letter-p">P</span>):
                </span>
                <strong className="summary-count">{practice.pending_patients || 0}</strong>
              </div>
              <div className="summary-divider-h"></div>

              <div
                className="summary-item"
                onClick={() => handleViewPatients(practice.id, "locked")}
              >
                <span className="summary-icon icon-locked">
                  <i className="fa fa-lock"></i>
                </span>
                <span className="summary-label">
                  Locked (<span className="letter-l">L</span>):
                </span>
                <strong className="summary-count">{practice.locked_patients || 0}</strong>
              </div>
            </div>
          </div>
        )
      },
      {
        key: 'actions',
        title: 'Action',
        sortable: false,
        render: (practice) => (
          <ActionButtons
            onView={() => handleViewPractice(practice.id)}
            onEdit={() => handleEditPractice(practice.id)}
            onDelete={() => handleDeletePractice(practice)}
            hideEdit={isSystemCaregiver}
            hideDelete={isSystemCaregiver}
            isDeleting={isDeleting && practiceToDelete?.id === practice.id}
          />
        )
      }
    ];
  }, [isDeleting, isSystemCaregiver, practiceToDelete, handleViewPractice, handleEditPractice, handleDeletePractice, handleViewPatients]);

  const entriesSelectValue =
    filters.per_page === 'all' || filters.per_page === 9999
      ? 'all'
      : String(filters.per_page || pagination?.per_page || 30);

  return (
    <div className="list-screen-page-container">
      {/* Top Bar: Title + Search + Add Button */}
      <div className="list-screen-top-bar">
        <div className="list-screen-title-section">
          <Heading title="Practices List" level="h2" className="list-screen-title-text" />
        </div>

        <div className="list-screen-actions">
          <ListScreenSearchBar
            value={localSearchTerm}
            onChange={setLocalSearchTerm}
            isLoading={isLoading}
            inputId="practices-list-search"
          />

          {!isSystemCaregiver && (
            <button type="button" className="list-screen-add-btn" onClick={handleAddPractice}>
              <FontAwesomeIcon icon={faPlus} className="list-screen-add-icon" />
              Add Practice
            </button>
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="practices-list-alert practices-list-alert-danger" role="alert">
          {error}
        </div>
      )}

      {/* Practices Table */}
      <Table
        columns={columns}
        data={sortedPractices}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSort={handleSort}
        isLoading={isLoading}
        loadingMode="spinner"
        emptyMessage="No practices found"
      />

      <ListScreenPaginationBar
        entriesValue={entriesSelectValue}
        onEntriesChange={(value) => {
          if (value === 'all') {
            dispatch(setFilters({ per_page: 'all' }));
          } else {
            dispatch(setFilters({ per_page: parseInt(value, 10) }));
          }
          setCurrentPage(1);
        }}
        leftSlot={
          !isSystemCaregiver && (
            <button type="button" className="list-screen-option-btn" onClick={() => setShowLogSection(!showLogSection)}>
              <FontAwesomeIcon icon={faList} className="list-screen-me-2" />
              Event Logs
            </button>
          )
        }
        showPageSelector={filters.per_page !== 'all' && filters.per_page !== 9999}
        currentPage={pagination?.current_page || 1}
        totalPages={pagination?.total_pages || 1}
        onPageChange={handlePageChange}
      />

      {/* Event Logs Modal Overlay */}
      {showLogSection && (
        <div className="practices-list-modal-overlay">
          <div className="practices-list-modal-container" style={{ maxWidth: '900px', width: '90%' }}>
            <div className="practices-list-modal-header">
              <h5 className="practices-list-modal-title">Event Logs</h5>
              <button
                type="button"
                className="practices-list-modal-close"
                onClick={() => setShowLogSection(false)}
              >
                ×
              </button>
            </div>
            <div className="practices-list-modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              <EventLogSection allModules />
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="practices-list-modal-overlay">
          <div className="practices-list-modal-container">
            <div className="practices-list-modal-header">
              <h5 className="practices-list-modal-title">Confirm Delete</h5>
              <button
                type="button"
                className="practices-list-modal-close"
                onClick={cancelDelete}
              >
                ×
              </button>
            </div>
            <div className="practices-list-modal-body">
              <p>
                Are you sure you want to delete the practice{' '}
                <strong>{practiceToDelete?.practice_name}</strong>?
              </p>
              <p className="practices-list-modal-warning">
                This action cannot be undone.
              </p>
            </div>
            <div className="practices-list-modal-footer">
              <button
                type="button"
                className="practices-list-btn-cancel"
                onClick={cancelDelete}
              >
                Cancel
              </button>
              <button
                type="button"
                className="practices-list-btn-delete"
                onClick={confirmDelete}
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin className="list-screen-me-2" />
                    Deleting...
                  </>
                ) : (
                  'Delete'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PracticesList;
